import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Link, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { KIND, download, errorText, fetchMerchants, fetchTatGrid } from './ptlSetupApi'
import { ROUTES, fmtDate, num, tatTabs } from './constants'
import { Banner } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import TatMatrix from './tat/TatMatrix'

const avg = (xs) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0)

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant TAT · Standard (plan screen 8). The Prozo standard TAT every PTL merchant
 * gets unless it has a custom TAT for the lane (ptl_lane_tat, merchantId null): KPI cards, the 16 x 16 matrix, download,
 * and the Upload / History screens. Custom TAT of merchants has its own screen.
 */
export default function PtlTatStandard() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [grid, setGrid] = useState(null)
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setError('')
    fetchTatGrid(null).then(setGrid).catch((e) => setError(errorText(e, 'Could not load the standard TAT')))
    fetchMerchants().then(setMerchants).catch(() => setMerchants([]))
  }, [])
  useEffect(() => { load() }, [load])

  const doDownload = async () => {
    try {
      const res = await download(KIND.TAT, null)
      saveBlob(res.data, filenameFromResponse(res, 'ptl_standard_tat.csv'))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), { variant: 'error' })
    }
  }

  const cells = grid?.cells || []
  const empty = grid && cells.length === 0
  const maxes = cells.map((c) => c.maxTat)
  const latest = grid?.latest
  const onCustom = (merchants || []).filter((m) => m.customTatLanes > 0).length
  const kpis = [
    { key: 'lanes', label: 'Lanes', value: num(cells.length), sub: `${num(grid?.zones?.length)} pickup x ${num(grid?.zones?.length)} drop zones` },
    { key: 'zones', label: 'Zones', value: num(grid?.zones?.length), sub: 'The PTL zones every map uses' },
    { key: 'avg', label: 'Average MAX TAT', value: cells.length ? `${avg(maxes).toFixed(1)} days` : '—', sub: cells.length ? `Average MIN ${avg(cells.map((c) => c.minTat)).toFixed(1)} days` : '' },
    { key: 'range', label: 'MAX TAT range', value: cells.length ? `${Math.min(...maxes)}-${Math.max(...maxes)} days` : '—', sub: 'Shortest to longest lane' },
    { key: 'custom', label: 'Merchants on custom TAT', value: merchants ? num(onCustom) : '', sub: 'Their agreement lanes override this', to: ROUTES.tatCustom },
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None', sub: latest ? latest.createdBy || '' : 'Not uploaded from this page yet' },
  ]

  return (
    <PtlPage title='Merchant TAT' tabTitle='Standard TAT' tabs={tatTabs(merchants ? onCustom : undefined)}
      description='The Prozo standard PTL TAT by lane (pickup zone to drop zone). Every PTL merchant gets it unless its agreement sets a custom TAT for the lane. It also defines the PTL zones the zone maps use.'
      actions={(
        <>
          <LoadingButton variant='outlined' startIcon={<DownloadOutlinedIcon />} disabled={!cells.length} onClick={() => triggerDownload(doDownload, 'Standard TAT')} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            Download TAT
          </LoadingButton>
          <Button variant='outlined' startIcon={<HistoryOutlinedIcon />} onClick={() => navigate(ROUTES.tatHistory)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>History</Button>
          <Button variant='contained' startIcon={<UploadFileOutlinedIcon />} disabled={!grid} onClick={() => navigate(ROUTES.tatUpload)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
            Upload standard TAT
          </Button>
        </>
      )}
      kpis={kpis} kpisLoading={!grid && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {empty ? (
        <EmptyState title='No standard TAT yet'
          body='Upload the Prozo Standard TAT file. It defines the PTL zones and the TAT of every lane; until then pricing keeps the TAT from the rate cards.'
          action={<Button variant='contained' startIcon={<UploadFileOutlinedIcon />} onClick={() => navigate(ROUTES.tatUpload)} sx={SX.cta}>Upload standard TAT</Button>} />
      ) : grid && (
        <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
          <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 1.5 }}>TAT by lane</Typography>
          <TatMatrix grid={grid} />
        </Card>
      )}
      <Typography sx={{ ...TYPE.rowHelp, mt: 1.5, color: C.inkMuted }}>
        A change applies to shipments priced after it is saved. Merchants with a custom TAT keep it on their custom lanes:{' '}
        <Link component={RouterLink} to={ROUTES.tatCustom}>see custom TAT</Link>.
      </Typography>
      {PolicyDialog}
    </PtlPage>
  )
}
